import { errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useFacility } from "@/lib/facility/facility";
import { invalidateKeys } from "@/lib/query/invalidate-keys";
import { orpc } from "@/lib/orpc";
import { optionalTrue, KEEP_PLACE } from "@/lib/search";
import { useCan } from "@/lib/policy";
import { BLANK_VENDOR, VendorDialog, type VendorDraft } from "./-components/vendor-dialog";
import { VendorTable } from "./-components/vendor-table";

export const Route = createFileRoute("/_authenticated/_financials/vendors/")({
  validateSearch: (search: Record<string, unknown>): { inactive?: true } => ({
    inactive: optionalTrue(search.inactive),
  }),
  component: Vendors,
});

const fail = (error: Error) => toast.error(errorWords(error));

/* A fixed catalogue rather than whatever somebody has already typed: a
   free-text type is how one building ends up with "Utility",
   "utilities" and "Util" as three kinds of vendor. */
const VENDOR_TYPES = [
  "Contractor",
  "Supplier",
  "Service Provider",
  "Utility",
  "Medical",
  "Insurance",
  "Other",
];

/** Who the building pays. */
function Vendors() {
  const { facilityId } = useFacility();
  const canWrite = useCan("financial:manage");
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<VendorDraft | null>(null);
  const navigate = useNavigate({ from: Route.fullPath });
  const showInactive = Boolean(Route.useSearch().inactive);
  const setShowInactive = (next: boolean) =>
    void navigate({
      search: { inactive: next ? true : undefined },
      ...KEEP_PLACE,
    });
  const [switchingOff, setSwitchingOff] = useState<{ id: string; name: string } | null>(null);

  const vendors = useQuery(orpc.financials.vendors.list.queryOptions({ input: { facilityId } }));
  /* The category a vendor's invoices land in by default. It is on the row as
     an id, which tells a reader nothing, so the catalogue is read alongside. */
  const categories = useQuery(orpc.financials.expenseCategories.list.queryOptions({}));
  const categoryName = (id: string | null) =>
    id ? (categories.data?.find((row) => row.id === id)?.name ?? "-") : "-";

  const deactivate = useMutation(
    orpc.financials.vendors.deactivate.mutationOptions({
      onSuccess: async () => {
        toast.success("Vendor switched off. Past entries keep their name.");
        await invalidateKeys(queryClient, [orpc.financials.key()]);
      },
      onError: fail,
    }),
  );

  /* Switched-off vendors stay on the record — their old invoices still point
     at them — so they are hidden rather than gone, and can be looked at. */
  const kinds = [
    ...new Set([
      ...VENDOR_TYPES,
      ...((vendors.data ?? []).map((row) => row.vendorType).filter(Boolean) as string[]),
    ]),
  ].toSorted();
  const shown = (vendors.data ?? []).filter((row) => showInactive || row.isActive);

  return (
    <>
      <PageHeader
        title="Vendors"
        count={vendors.data ? shown.length : undefined}
        meta={
          vendors.data && shown.length !== vendors.data.length
            ? `Showing ${shown.length} of ${vendors.data.length}, who the building pays, and what they are usually booked to.`
            : "Who the building pays, and what they are usually booked to."
        }
        actions={
          canWrite ? <Button onClick={() => setEditing(BLANK_VENDOR)}>Add a vendor</Button> : null
        }
      />

      {vendors.isError ? (
        <ReadFailed
          message="Vendors could not be loaded."
          error={vendors.error}
          onRetry={() => void vendors.refetch()}
          retrying={vendors.isFetching}
        />
      ) : (
        <VendorTable
          vendors={shown}
          pending={vendors.isPending}
          canWrite={canWrite}
          categoryName={categoryName}
          onEdit={setEditing}
          onSwitchOff={setSwitchingOff}
          trailing={
            <div className="flex items-center gap-2">
              <Switch id="show-inactive" checked={showInactive} onCheckedChange={setShowInactive} />
              <Label htmlFor="show-inactive" className="cursor-pointer text-sm">
                Show switched off
              </Label>
            </div>
          }
          empty={
            (vendors.data ?? []).length > 0 ? (
              <EmptyState
                title="No vendors match"
                description="Every vendor on file is switched off."
                action={
                  <Button variant="outline" size="sm" onClick={() => setShowInactive(true)}>
                    Show switched off
                  </Button>
                }
              />
            ) : (
              <EmptyState title="No vendors yet" description="Add the people the building pays." />
            )
          }
        />
      )}

      {switchingOff ? (
        <RemoveConfirm
          title={`Switch off ${switchingOff.name}?`}
          confirmLabel="Switch off vendor"
          description={`${switchingOff.name} stops being offered on new entries. Every entry already booked to them keeps their name, and you can switch them back on from Edit.`}
          onConfirm={() => deactivate.mutate({ vendorId: switchingOff.id })}
          onClose={() => setSwitchingOff(null)}
        />
      ) : null}

      {editing ? (
        <VendorDialog
          initial={editing}
          facilityId={facilityId}
          kinds={kinds}
          categories={categories.data ?? []}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}
